
import { BaseDataDTO, LocationMapDTO, MarketDTO, PeriodCurvesDTO, PeriodDTO } from "src/data/physical-markets/DTO/DTO";
import { mapperBaseDataDTOToModel, mapperLocationMapDTOModel, mapperMarketsDTOToModel, mapperPeriodCurveDTOToModel, mapperPeriodDTOToModel } from "src/data/physical-markets/DTO/DTOToModel";
import { BaseData, DailyPricing, LocationMapViewModel, Market, Period, PeriodCurve, SendReportResult } from "src/@type/physical-markets/Model";
import useAuth from "src/hooks/useAuth";
import { createContext, ReactNode } from "react";

type DailyPricingDataContextType = {
    getMaterials: () => Promise<BaseData>
    getMarkets:  (originID: number, materialID: number) => Promise<Market[]>
    insertPeriod: (period: Period) => Promise<Period>
    updatePeriod: (period: Period) => Promise<Period> 
    getPeriod: (id: number) => Promise<Period> 
    getPeriodCurves: (id: number, allFutureHistory: boolean) => Promise<PeriodCurve>
    insertDailyPricing: (dailyPricing: DailyPricing) => Promise<Period>
    updateDailyPricing: (dailyPricing: DailyPricing) => Promise<Period>
    getLocationMap: (materialID: number) => Promise<LocationMapViewModel>
    sendReport: (marketID: number) => Promise<SendReportResult>
};

export const DailyPricingDataContext = createContext<DailyPricingDataContextType | null>(null);

type DailyPricingDataContextProviderProps = {
    children: ReactNode;
};

export function DailyPricingDataContextProvider({ children } : DailyPricingDataContextProviderProps) {
    const { axios } = useAuth();

    const getMaterials = () => new Promise<BaseData>((resolve, reject) => {
        axios.get('/mid/physical-markets/material/')
        .then(response => {
            const { data, error } = response.data;
            if(error) reject(error);
            else resolve(mapperBaseDataDTOToModel(data as BaseDataDTO));
        }).catch(err => {
            reject(err.message);
        });
    });
    
    const getMarkets = (originID: number, materialID: number) => new Promise<Market[]>((resolve, reject) => {
        axios.get('/mid/physical-markets/market/', {
            params: {
                originID: originID,
                materialID: materialID
            }
        })
        .then(response => {
            const { data, error } = response.data;
            if(error) reject(error);
            else resolve(mapperMarketsDTOToModel(data as MarketDTO[]));
        }).catch(err => {
            reject(err.message);
        });
    });

    const insertPeriod = (period: Period) => new Promise<Period>((resolve, reject) => {
        axios.put('/mid/physical-markets/period/', period).then(response => {
            const { data, error } = response.data;
            if (error) reject(error);
            else resolve(mapperPeriodDTOToModel(data as PeriodDTO));
        }).catch(err => {
            reject(err.message);
        });
    });
    
    const updatePeriod = (period: Period) => new Promise<Period>((resolve, reject) => {
        axios.post('/mid/physical-markets/period/', period).then(response => {
            const { data, error } = response.data;
            if (error) reject(error);
            else resolve(mapperPeriodDTOToModel(data as PeriodDTO));
        }).catch(err => {
            reject(err.message);
        });
    });
    
    const getPeriod = (id: number) => new Promise<Period>((resolve, reject) => {
        axios.get('/mid/physical-markets/period/', {
            params: {
                id: id
            }
        }).then(response => {
            const { data, error } = response.data;
            if (error) reject(error);
            else resolve(mapperPeriodDTOToModel(data as PeriodDTO));
        }).catch(err => {
            reject(err.message);
        });
    });
    
    const getPeriodCurves = (id: number, allFutureHistory: boolean) => new Promise<PeriodCurve>((resolve, reject) => {
        axios.get('/mid/physical-markets/period/curves/', {
            params: {
                id: id,
                allFutureHistory: allFutureHistory
            }
        }).then(response => {
            const { data, error } = response.data;
            if (error) reject(error);
            else resolve(mapperPeriodCurveDTOToModel(data as PeriodCurvesDTO));
        }).catch(err => {
            reject(err.message);
        });
    });
    
    const insertDailyPricing = (dailyPricing: DailyPricing) => new Promise<Period>((resolve, reject) => {
        axios.put('/mid/physical-markets/daily-pricing/', dailyPricing).then(response => {
            const { data, error } = response.data;
            if (error) reject(error);
            else resolve(mapperPeriodDTOToModel(data as PeriodDTO));
        }).catch(err => {
            reject(err.message);
        });
    });
    
    const updateDailyPricing = async(dailyPricing: DailyPricing) => new Promise<Period>((resolve, reject) => {
        axios.post('/mid/physical-markets/daily-pricing/', dailyPricing).then(response => {
            const { data, error } = response.data;
            if (error) reject(error);
            else resolve(mapperPeriodDTOToModel(data as PeriodDTO));
        }).catch(err => {
            reject(err.message);
        });
    });
    
    const getLocationMap = async (materialID: number) => new Promise<LocationMapViewModel>((resolve, reject) => {
        axios.get('/mid/physical-markets/mapLocation/', {
            params: {
                materialID: materialID
            }
        }).then(response => {
            const { data, error } = response.data;
            if (error) reject(error);
            else resolve(mapperLocationMapDTOModel(data as LocationMapDTO));
        }).catch(err => {
            reject(err.message);
        });
    });

    const sendReport = async (marketID: number) => new Promise<SendReportResult>((resolve, reject) => {
        axios.post('/mid/physical-markets/market/sendReportMail/', { marketID: marketID }).then(response => {
            const { data, error } = response.data;
            if (error) reject(error);
            else resolve(data);
        }).catch(err => {
            reject(err.message);
        });
    });

    return (
        <DailyPricingDataContext.Provider
          value={{
            getMaterials,
            getMarkets,
            insertPeriod,
            updatePeriod,
            getPeriod,
            getPeriodCurves,
            insertDailyPricing,
            updateDailyPricing,
            getLocationMap,
            sendReport
          }}
        >
            {children}
        </DailyPricingDataContext.Provider>
      );
}